import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "@src/api/config";
import { showModalSuccess } from "@src/redux/modalTypeSuccess";
import { AppDispatch, RootState } from "@src/redux/store";
import { EmployeeBeneficiary, EmployeeBeneficiaryFormValues } from "../types";
import { employeeBeneficiariesResource } from "../resource";

type RequestParams = Record<string, any>;

interface ListParams {
  employeeId: number | string;
  params?: RequestParams;
}

interface CreateParams {
  employeeId?: number | string;
  data: EmployeeBeneficiaryFormValues;
  successMessage?: string;
}

interface UpdateParams {
  id: number | string;
  data: EmployeeBeneficiaryFormValues;
}

interface DeleteParams {
  id: number | string;
}

interface EmployeeBeneficiariesState {
  data: EmployeeBeneficiary[];
  current: EmployeeBeneficiary | null;
  loading: boolean;
  params: RequestParams;
  meta: any;
  total: number;
}

const getRows = (payload: any): EmployeeBeneficiary[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
};

export const getEmployeeBeneficiaries = createAsyncThunk<
  any,
  ListParams,
  { state: RootState }
>("employeeBeneficiaries/getAll", async ({ employeeId, params = {} }) => {
  const response = await api.get(employeeBeneficiariesResource.list(employeeId), {
    params
  });

  return {
    data: response.data,
    params
  };
});

export const getEmployeeBeneficiaryById = createAsyncThunk<any, number | string>(
  "employeeBeneficiaries/getById",
  async (id) => {
    const response = await api.get(employeeBeneficiariesResource.show(id));

    return {
      data: response.data
    };
  }
);

export const createEmployeeBeneficiary = createAsyncThunk<
  any,
  CreateParams,
  { dispatch: AppDispatch; state: RootState }
>("employeeBeneficiaries/create", async ({ employeeId, data, successMessage }, { dispatch }) => {
  const payload = employeeId
    ? data
    : {
        ...data,
        employee_id: data.employee_id
      };
  const response = await api.post(
    employeeBeneficiariesResource.create(employeeId),
    payload
  );

  await dispatch(
    showModalSuccess({
      message:
        successMessage ??
        response.data?.message ??
        "Beneficiario guardado correctamente"
    })
  );

  return {
    data: response.data
  };
});

export const updateEmployeeBeneficiary = createAsyncThunk<
  any,
  UpdateParams,
  { dispatch: AppDispatch; state: RootState }
>("employeeBeneficiaries/update", async ({ id, data }, { dispatch }) => {
  const response = await api.put(employeeBeneficiariesResource.update(id), data);

  await dispatch(
    showModalSuccess({
      message: response.data?.message ?? "Beneficiario actualizado correctamente"
    })
  );

  return {
    data: response.data
  };
});

export const deleteEmployeeBeneficiary = createAsyncThunk<
  any,
  DeleteParams,
  { dispatch: AppDispatch; state: RootState }
>("employeeBeneficiaries/delete", async ({ id }, { dispatch }) => {
  const response = await api.delete(employeeBeneficiariesResource.delete(id));

  await dispatch(
    showModalSuccess({
      message: response.data?.message ?? "Beneficiario eliminado correctamente"
    })
  );

  return {
    data: response.data,
    id
  };
});

const initialState: EmployeeBeneficiariesState = {
  data: [],
  current: null,
  loading: false,
  params: {},
  meta: null,
  total: 0
};

const employeeBeneficiariesSlice = createSlice({
  name: "employeeBeneficiaries",
  initialState,
  reducers: {
    clearEmployeeBeneficiaries: (state) => {
      state.data = [];
      state.current = null;
      state.params = {};
      state.meta = null;
      state.total = 0;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getEmployeeBeneficiaries.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getEmployeeBeneficiaries.fulfilled, (state, action) => {
      state.loading = false;
      state.data = getRows(action.payload.data);
      state.params = action.payload.params;
      state.meta = action.payload.data?.meta ?? action.payload.data?.data?.meta;
      state.total = state.meta?.total ?? state.data.length;
    });
    builder.addCase(getEmployeeBeneficiaries.rejected, (state) => {
      state.loading = false;
    });
    builder.addCase(getEmployeeBeneficiaryById.fulfilled, (state, action) => {
      state.current = action.payload?.data?.data ?? action.payload?.data ?? null;
    });
    builder.addCase(createEmployeeBeneficiary.fulfilled, (state, action) => {
      const beneficiary = action.payload?.data?.data ?? action.payload?.data;
      if (beneficiary) {
        state.data = [...state.data, beneficiary];
      }
    });
    builder.addCase(updateEmployeeBeneficiary.fulfilled, (state, action) => {
      const beneficiary = action.payload?.data?.data ?? action.payload?.data;
      const beneficiaryId = beneficiary?.id;

      if (beneficiaryId) {
        state.data = state.data.map((item) => (
          String(item.id) === String(beneficiaryId) ? beneficiary : item
        ));
      }
    });
    builder.addCase(deleteEmployeeBeneficiary.fulfilled, (state, action) => {
      state.data = state.data.filter(
        (item) => String(item.id) !== String(action.payload.id)
      );
    });
  }
});

export const { clearEmployeeBeneficiaries } = employeeBeneficiariesSlice.actions;

export default employeeBeneficiariesSlice.reducer;
